import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  LoadingOutlined,
  SyncOutlined as ProcessingOutlined
} from '@ant-design/icons';
import { blue, green, red, yellow } from '@ant-design/colors';
import { Button, Spin } from 'antd';
import React, { useCallback, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DeepResearch, DeepResearchStatus } from '../../../../types/src';
import { formatTimeAgo } from '../../utils/formatTimeAgo';

interface ResearchHistoryListProps {
  researchHistory: DeepResearch[];
  loading: boolean;
  researchHistoryMetaData: {
    count: number;
    page_size: number;
    page_count: number;
    has_next: boolean;
    next: number;
  };
  getResearch: (prospect_id: number, page: number, pageSize: number) => void;
  onDownloadClick: (params: {id: string, fileUrl: string}) => void;
  prospectId: number;
}

export const ResearchHistoryList: React.FC<ResearchHistoryListProps> = ({
  researchHistory,
  loading,
  getResearch,
  researchHistoryMetaData,
  onDownloadClick,
  prospectId
}) => {
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMore = useCallback(() => {
    if (loadingMore || loading || !researchHistoryMetaData.has_next) {
      return;
    }

    setLoadingMore(true);
    getResearch(prospectId, researchHistoryMetaData.next, researchHistoryMetaData.page_size);

    // Reset loading state after a short delay to prevent rapid calls
    setTimeout(() => {
      setLoadingMore(false);
    }, 1000);
  }, [loadingMore, loading, researchHistoryMetaData, getResearch, prospectId]);

  const hasMore = researchHistoryMetaData.has_next && !loading;

  const getResearchStatusInfo = (status: number) => {
    switch (status) {
      case DeepResearchStatus.NOTIFICATION_RECEIVED:
        return {
          icon: <ExclamationCircleOutlined style={{ color: blue[6], fontSize: '12px' }} />,
          text: 'Notified',
          color: blue[6],
          bgColor: blue[1],
          canDownload: false,
          showLoading: false
        };
      case DeepResearchStatus.PROCESSING:
        return {
          icon: <LoadingOutlined style={{ color: yellow[6], fontSize: '12px' }} />,
          text: 'Processing',
          color: yellow[6],
          bgColor: yellow[1],
          canDownload: false,
          showLoading: true
        };
      case DeepResearchStatus.PROCESSED:
        return {
          icon: <CheckCircleOutlined style={{ color: green[6], fontSize: '12px' }} />,
          text: 'Completed',
          color: green[6],
          bgColor: green[1],
          canDownload: true,
          showLoading: false
        };
      case DeepResearchStatus.FAILED:
        return {
          icon: <CloseCircleOutlined style={{ color: red[6], fontSize: '12px' }} />,
          text: 'Failed',
          color: red[6],
          bgColor: red[1],
          canDownload: false,
          showLoading: false
        };
      case DeepResearchStatus.INITIALIZED:
        return {
          icon: <ProcessingOutlined style={{ color: blue[6], fontSize: '12px' }} />,
          text: 'Starting',
          color: blue[6],
          bgColor: blue[1],
          canDownload: false,
          showLoading: true
        };
      case DeepResearchStatus.REINDEXING:
        return {
          icon: <ProcessingOutlined spin style={{ color: yellow[6], fontSize: '12px' }} />,
          text: 'Reindexing',
          color: yellow[6],
          bgColor: yellow[1],
          canDownload: false,
          showLoading: true
        };
      default:
        return {
          icon: <ExclamationCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px' }} />,
          text: 'Unknown',
          color: '#8c8c8c',
          bgColor: '#f5f5f5',
          canDownload: false,
          showLoading: false
        };
    }
  };

  // if (loading) {
  //   return (
  //     <div style={{ marginTop: '16px', textAlign: 'center' }}>
  //       <Spin size="small" />
  //       <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '4px' }}>
  //         Loading research history...
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{
        fontSize: '13px',
        fontWeight: '500',
        color: '#777',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <FileTextOutlined style={{ fontSize: '12px' }} />
        Research History ({researchHistoryMetaData.count}) {loading && <Spin size="small" style={{ marginRight: '8px' }} />}
      </div>
      <div id="research-list-container" style={{ maxHeight: '150px', overflowY: 'auto' }}>
        <InfiniteScroll
          dataLength={researchHistory.length}
          next={loadMore}
          hasMore={hasMore}
          loader={
            <div style={{
              textAlign: 'center',
              padding: '12px 0',
              fontSize: '11px',
              color: '#8c8c8c'
            }}>
              {loading && (
                <>
                  <Spin size="small" style={{ marginRight: '8px' }} />
                  Loading more research...
                </>
              )}
            </div>
          }
          endMessage={
            researchHistory.length > 0 && !hasMore ? (
              <div style={{
                textAlign: 'center',
                padding: '12px 0',
                fontSize: '10px',
                color: '#bfbfbf',
                borderTop: '1px solid #f0f0f0',
                marginTop: '8px'
              }}>
                No more research history
              </div>
            ) : null
          }
          scrollThreshold={0.9}
          style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
          scrollableTarget="research-list-container"
        >
          {researchHistory
            .map((research, index) => {
              const statusInfo = getResearchStatusInfo(research.status);
              return (
                <div
                  key={research.id}
                  onClick={() => statusInfo.canDownload && onDownloadClick({id: research.id, fileUrl: research.deep_research_analysis_file_url})}
                  style={{
                    cursor: statusInfo.canDownload ? 'pointer' : 'default',
                    padding: '8px 10px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: '36px',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flex: 1,
                    minWidth: 0
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {statusInfo.showLoading ? (
                        <Spin indicator={<LoadingOutlined spin />} size="small" style={{ fontSize: '10px' }} />
                      ) : (
                        statusInfo.icon
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: '500',
                        color: '#777',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{
                          color: statusInfo.color,
                          fontSize: '10px'
                        }}>
                          {statusInfo.text}
                        </span>
                        <div style={{
                          fontSize: '9px',
                          color: '#8c8c8c',
                          marginTop: '1px'
                        }}>
                          {formatTimeAgo(research.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                  {statusInfo.canDownload && research.deep_research_analysis_file_url && (
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined style={{ fontSize: '10px' }} />}
                      onClick={() => onDownloadClick({id: research.id, fileUrl: research.deep_research_analysis_file_url})}
                      style={{
                        padding: '2px 4px',
                        height: '20px',
                        minWidth: '20px',
                        color: statusInfo.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    />
                  )}
                </div>
              );
            })}
        </InfiniteScroll>
      </div>
    </div>
  );
};
